import { useState, useEffect } from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const DISMISS_KEY = 'kp_onboarding_dismissed';

/**
 * Shown on Dashboard ONLY if:
 *  - User has email
 *  - That email exists at Karrierepasset
 *  - User has not yet linked
 *  - User hasn't dismissed banner this session
 *
 * Silently does nothing otherwise — does not disturb users without KP-konto.
 */
export function KarrierepassetOnboardingBanner() {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    // Check session dismiss
    if (sessionStorage.getItem(DISMISS_KEY) === '1') {
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        const res = await fetch('/api/candidate/karrierepasset/lookup-on-onboarding', {
          credentials: 'include',
        });
        if (res.status === 503) { if (mounted) setLoading(false); return; }
        const data = await res.json();
        if (!mounted) return;
        if (data.already_linked) { setLoading(false); return; }
        if (data.exists === true) {
          setEmail(data.email || null);
          setShow(true);
        }
        setLoading(false);
      } catch {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  function handleDismiss() {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  }

  if (loading || !show) return null;

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-5 relative">
      <button
        onClick={handleDismiss}
        aria-label="Lukk varsel"
        className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-700 hover:bg-white/50 rounded transition"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 pr-8">
        <Sparkles className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-bold text-gray-900">Du har en Karrierepasset-konto</h3>
          <p className="text-sm text-gray-700 mt-1">
            {email && (
              <>Vi fant en Karrierepasset-konto knyttet til <strong>{email}</strong>. </>
            )}
            Koble den til for å berike CV-en og kursanbefalingene dine med personlighet,
            karriereinteresser og persona — og send kompetanseattestene dine direkte til Karrierepasset.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Link
              to="/settings"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
            >
              Koble til Karrierepasset
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-gray-600 hover:bg-white/50 rounded text-sm"
            >
              Ikke nå
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
